/**
 * ComparisonShowcase — Premium renderer for comparison / competitive layouts.
 * Palette: violet (option A) + orange (option B).
 * Migrated to Athena Presentation Design System (Phase 7.5).
 */
import React from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  a:       '#a78bfa',
  aFade:   'rgba(167,139,250,0.10)',
  aBdr:    'rgba(167,139,250,0.22)',
  b:       '#fb923c',
  bFade:   'rgba(251,146,60,0.10)',
  bBdr:    'rgba(251,146,60,0.22)',
  success: '#34d399',
  card:    base.card,
  border:  base.border,
  text:    base.text,
  textMuted: base.textMuted,
  textFaint: base.textFaint,
};

const _card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.2)' };

function SubjectTag({ label, color, fade, bdr }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color, background: fade, border: `1px solid ${bdr}`, borderRadius: 6, padding: '3px 10px' }}>
      {label}
    </span>
  );
}

function HeroDesktop({ d }) {
  const titleFs = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const la = d.subject_a || 'Opção A';
  const lb = d.subject_b || 'Opção B';
  const METRICS = [
    { icon: '🟣', value: la, label: 'Opção A' },
    { icon: '🟠', value: lb, label: 'Opção B' },
    { icon: '📊', value: (d.dimensions || []).length > 0 ? `${d.dimensions.length} critérios` : 'Análise', label: 'Dimensões' },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="⚖️ COMPARAÇÃO"
        accentColor={C.a} accentFade={C.aFade} accentBdr={C.aBdr}
        placeholder="⚖️" placeholderLabel="Comparação"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <h1 style={{ margin: '0 0 10px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '0 0 10px', fontSize: 13, color: base.textSec, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <SubjectTag label={la} color={C.a} fade={C.aFade} bdr={C.aBdr} />
            <span style={{ color: base.textFaint, fontSize: 12, fontWeight: 700 }}>vs</span>
            <SubjectTag label={lb} color={C.b} fade={C.bFade} bdr={C.bBdr} />
          </div>
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.a} />
      </div>
    </div>
  );
}

function HeroMobile({ d }) {
  const la = d.subject_a || 'Opção A';
  const lb = d.subject_b || 'Opção B';
  const METRICS = [
    { icon: '🟣', value: la, label: 'Opção A' },
    { icon: '🟠', value: lb, label: 'Opção B' },
    { icon: '📊', value: (d.dimensions || []).length > 0 ? `${d.dimensions.length}` : '-', label: 'Critérios' },
    { icon: '⚖️', value: 'Análise', label: 'Tipo' },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 12, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.a, background: C.aFade, border: `1px solid ${C.aBdr}`, borderRadius: 5, padding: '2px 8px', marginBottom: 10 }}>⚖️ COMPARAÇÃO</span>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <SubjectTag label={la} color={C.a} fade={C.aFade} bdr={C.aBdr} />
            <span style={{ color: base.textFaint, fontSize: 11, fontWeight: 700 }}>vs</span>
            <SubjectTag label={lb} color={C.b} fade={C.bFade} bdr={C.bBdr} />
          </div>
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.a} accentFade={C.aFade} accentBdr={C.aBdr}
          placeholder="⚖️"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.a} />
      </div>
    </div>
  );
}

function DimensionRow({ dim, label_a, label_b, index }) {
  const evenBg = index % 2 === 0 ? 'rgba(167,139,250,0.03)' : 'transparent';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', padding: '9px 10px', background: evenBg, borderRadius: 6 }}>
      <div style={{ padding: '4px 8px', background: C.aFade, border: `1px solid ${C.aBdr}`, borderRadius: 6, fontSize: 13, color: C.text, textAlign: 'center', lineHeight: 1.4 }}>{dim.value_a || '—'}</div>
      <div style={{ width: 40, textAlign: 'center', fontSize: 10, color: base.textFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', padding: '0 4px', lineHeight: 1.3 }}>{dim.label}</div>
      <div style={{ padding: '4px 8px', background: C.bFade, border: `1px solid ${C.bBdr}`, borderRadius: 6, fontSize: 13, color: C.text, textAlign: 'center', lineHeight: 1.4 }}>{dim.value_b || '—'}</div>
    </div>
  );
}

function DimensionRowMobile({ dim, label_a, label_b }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: base.textFaint, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{dim.label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, padding: '6px 8px', background: C.aFade, border: `1px solid ${C.aBdr}`, borderRadius: 6, fontSize: 12, color: C.text }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.a, marginBottom: 3, letterSpacing: '.06em' }}>{label_a}</div>
          {dim.value_a || '—'}
        </div>
        <div style={{ flex: 1, padding: '6px 8px', background: C.bFade, border: `1px solid ${C.bBdr}`, borderRadius: 6, fontSize: 12, color: C.text }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.b, marginBottom: 3, letterSpacing: '.06em' }}>{label_b}</div>
          {dim.value_b || '—'}
        </div>
      </div>
    </div>
  );
}

function ItemList({ title, items, color, fade, bdr }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${bdr}`, borderLeft: `3px solid ${color}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color, marginBottom: 8 }}>{title}</div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {items.map((it, i) => (
          <li key={i} style={{ display: 'flex', gap: 7, fontSize: 13, color: C.text }}>
            <span style={{ color, flexShrink: 0, marginTop: 3 }}>•</span>
            <span style={{ lineHeight: 1.5 }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ComparisonShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  const la = d.subject_a || 'Opção A';
  const lb = d.subject_b || 'Opção B';
  const dims = d.dimensions || [];

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <HeroMobile d={d} /> : <HeroDesktop d={d} />}

      {d.context && !d.subtitle && (
        <div style={{ ..._card, padding: '12px 14px', marginBottom: 12, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.context}</div>
      )}

      {dims.length > 0 && (
        <div style={{ ..._card, padding: '12px', marginBottom: 12 }}>
          {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.a, textAlign: 'center', letterSpacing: '.05em', textTransform: 'uppercase' }}>{la}</div>
              <div style={{ width: 40 }} />
              <div style={{ fontSize: 12, fontWeight: 800, color: C.b, textAlign: 'center', letterSpacing: '.05em', textTransform: 'uppercase' }}>{lb}</div>
            </div>
          )}
          {dims.map((dim, i) =>
            isMobile
              ? <DimensionRowMobile key={i} dim={dim} label_a={la} label_b={lb} />
              : <DimensionRow key={i} dim={dim} label_a={la} label_b={lb} index={i} />
          )}
        </div>
      )}

      {(d.advantages_a || d.disadvantages_a) && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <ItemList title={`✅ Vantagens de ${la}`} items={d.advantages_a} color={C.a} fade={C.aFade} bdr={C.aBdr} />
          <ItemList title={`⚠️ Desvantagens de ${la}`} items={d.disadvantages_a} color={C.b} fade={C.bFade} bdr={C.bBdr} />
        </div>
      )}

      {(d.recommendation || d.winner) && (
        <div style={{ background: `linear-gradient(135deg, ${C.aFade}, ${C.bFade})`, border: `1px solid ${C.aBdr}`, borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
          {d.winner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: d.recommendation ? 8 : 0 }}>
              <span style={{ fontSize: 14, color: C.textMuted }}>Vencedor:</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: d.winner === 'a' ? C.a : C.b }}>
                🏆 {d.winner === 'a' ? la : lb}
              </span>
            </div>
          )}
          {d.recommendation && <p style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.recommendation}</p>}
        </div>
      )}

      {d.conclusion && d.conclusion !== d.recommendation && (
        <div style={{ ..._card, padding: '12px 14px', borderLeft: `3px solid ${C.success}`, marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.success, marginBottom: 8 }}>Conclusão</div>
          <p style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.conclusion}</p>
        </div>
      )}

      {(d.sections || []).map((s, i) => (
        <div key={i} style={{ ..._card, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 8 }}>{s.title}</div>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </div>
      ))}

      <AthenaTip tip={d.tip} accentColor={C.a} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.a} />
    </div>
  );
}
